import { useMutation } from "@tanstack/react-query";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { AFTER_LOGIN } from "@/components/Layout";
import { Button, Card, ErrorText, Spinner } from "@/components/ui";
import { api } from "@/lib/api";
import { useMe } from "@/lib/auth";

/** The companion's "Connect in browser": on approval a one-time code goes to the app on this PC, never anywhere else. */
export default function ConnectCompanion() {
  const me = useMe();
  const location = useLocation();
  const [params] = useSearchParams();
  const port = Number(params.get("port"));
  const state = params.get("state") ?? "";
  const challenge = params.get("challenge") ?? "";
  const name = (params.get("name") ?? "").slice(0, 100).trim() || "Companion";
  const valid = Number.isInteger(port) && port >= 1024 && port <= 65535 && /^[\w-]{16,100}$/.test(state) && /^[\w-]{43}$/.test(challenge);

  function back(query: Record<string, string>) {
    window.location.replace(`http://127.0.0.1:${port}/callback?${new URLSearchParams({ ...query, state })}`);
  }
  const allow = useMutation({
    mutationFn: () => api.post<{ code: string }>("/api/companion/authorize", { challenge, name }),
    onSuccess: ({ code }) => back({ code }),
  });

  if (me.isLoading)
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size={24} />
      </div>
    );
  if (!me.data) {
    // Also survives a single sign-on round trip, in this tab.
    sessionStorage.setItem(AFTER_LOGIN, location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }
  return (
    <div className="flex h-full items-center justify-center overflow-y-auto p-4">
      <div className="w-full max-w-md">
        <Card title="Connect the desktop companion">
          {valid ? (
            <>
              <p className="mb-3 text-sm">
                <b className="font-medium">{name}</b> asks to use Home AI as <b className="font-medium">{me.data.name}</b>.
              </p>
              <p className="mb-4 text-sm text-zinc-500">
                Only allow this if you just pressed "Connect in browser" in the companion. It gets an API token that acts as you for chats and models. You can
                revoke it under Settings → General → API tokens.
              </p>
              <ErrorText error={allow.error} />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="secondary" disabled={allow.isPending} onClick={() => back({ error: "denied" })}>
                  Cancel
                </Button>
                <Button type="button" disabled={allow.isPending || allow.isSuccess} onClick={() => allow.mutate()}>
                  Allow
                </Button>
              </div>
            </>
          ) : (
            <p className="text-sm text-zinc-500">This link is not valid. Start again from the companion's settings.</p>
          )}
        </Card>
      </div>
    </div>
  );
}
